import { useEffect, useRef } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { viewItemEvent } from '@lib/utils/exponeaTrackingEvents'
import { PDP_REVISIT_EVENT } from './pdpEvents'

type DataLayerActionInput = {
    type: 'PDP'
    data: {
        type: 'ProductDetailsImpressions'
        id?: string
        name?: string
        categories?: unknown
        price?: number
        currencyCode?: string
        category?: { name: string | null }
    }
}

type UsePdpLifecycleEffectsParams = {
    asPath?: string
    productUrl?: string
    /** Use replace (not push) so back navigation does not loop on URL without .html. */
    replace: (url: string) => void
    product?: any
    variant?: any
    dataLayerAction: (input: DataLayerActionInput) => void
    setQuantity?: Dispatch<SetStateAction<number>>
}

export function usePdpLifecycleEffects({
    asPath,
    productUrl,
    replace,
    product,
    variant,
    dataLayerAction,
    setQuantity
}: UsePdpLifecycleEffectsParams): void {
    const dataLayerActionRef = useRef(true)
    const replaceRef = useRef(replace)
    replaceRef.current = replace
    const htmlReplaceIssuedFor = useRef<string | null>(null)

    useEffect(() => {
        if (!setQuantity) return
        setQuantity(1)
    }, [productUrl, asPath, setQuantity])

    useEffect(() => {
        if (!setQuantity) return

        const handlePdpRevisit = () => setQuantity(1)

        window.addEventListener(PDP_REVISIT_EVENT, handlePdpRevisit)
        return () =>
            window.removeEventListener(PDP_REVISIT_EVENT, handlePdpRevisit)
    }, [setQuantity])

    useEffect(() => {
        const currentPath = asPath || ''
        if (!currentPath) return
        const pathWithoutQuery = currentPath.split('?')[0]
        const hasHtml = pathWithoutQuery.includes('.html')
        const hasHash = currentPath.includes('#')
        if (hasHtml || hasHash) {
            htmlReplaceIssuedFor.current = null
            return
        }
        if (htmlReplaceIssuedFor.current === currentPath) return
        htmlReplaceIssuedFor.current = currentPath
        replaceRef.current(currentPath + '.html')
    }, [asPath])

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)')

        const syncLauncher = () => {
            const launcher = document.getElementById('launcher')
            if (!launcher) return
            if (mq.matches) {
                launcher.style.display = 'none'
            } else {
                launcher.style.removeProperty('display')
            }
        }

        syncLauncher()
        const onMq = () => syncLauncher()
        mq.addEventListener('change', onMq)

        // Observa el DOM solo hasta que Zendesk inyecte #launcher; evita
        // MutationObserver permanente en todo el subtree (costoso en PDP).
        let observer: MutationObserver | null = null
        if (!document.getElementById('launcher')) {
            observer = new MutationObserver(() => {
                if (!document.getElementById('launcher')) return
                syncLauncher()
                observer?.disconnect()
                observer = null
            })
            observer.observe(document.body, { childList: true, subtree: true })
        }

        return () => {
            mq.removeEventListener('change', onMq)
            observer?.disconnect()
            const launcher = document.getElementById('launcher')
            if (launcher) {
                launcher.style.removeProperty('display')
            }
        }
    }, [])

    useEffect(() => {
        if (!product || !dataLayerActionRef.current) return
        const finalPriceInformation =
            product?.price_range?.minimum_price?.final_price
        const categoryName = sessionStorage?.getItem('origin-category')
        dataLayerAction({
            type: 'PDP',
            data: {
                type: 'ProductDetailsImpressions',
                id: variant?.sku,
                name: product.name,
                categories: product.categories,
                price: finalPriceInformation?.value,
                currencyCode: finalPriceInformation?.currency,
                category: { name: categoryName }
            }
        })
        dataLayerActionRef.current = false
    }, [dataLayerAction, product, variant?.sku])

    useEffect(() => {
        if (!product) return
        viewItemEvent(product)
    }, [product])

    useEffect(() => {
        if (!product || !variant?.sku || variant.sku === product.sku) return
        const viewedProd = Object.assign({}, product)
        viewedProd.id = variant.id
        viewedProd.sku = variant.sku
        viewedProd.price_range = variant.price_range
        viewedProd.is_set = variant.is_set
        viewedProd.ppum = variant.ppum
        viewItemEvent(viewedProd)
    }, [product, variant?.sku])
}
